import { MaterialCommunityIcons } from '@expo/vector-icons';
import { IssueType } from '@gripp/shared-logic';
import {
  ActionMenuList,
  ActionMenuRow,
  CallModeButton,
  Colors,
  Icon,
  IssueHeaderProps,
  severityToVariant,
  SlideAction,
  tokens,
  TypeDropdown,
  TypeVariant,
  useSlideLayout,
  variantToSeverity,
} from '@gripp/shared-ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Menu } from 'react-native-paper';
import { useLocation, useNavigate } from 'react-router-dom';

export const IssueHeaderCropCircle: React.FC<IssueHeaderProps> = (
  props: IssueHeaderProps
) => {
  const { onSlideChange, prepareSlideTransition, column } = useSlideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const issue = props.issue;

  const [currentSeverity, setSeverity] = useState(issue.severity);
  const [menuVisible, setMenuVisible] = useState(false);

  useEffect(() => {
    setSeverity(issue.severity);
  }, [issue]);

  const handleSeverityChange = (variant: TypeVariant) => {
    const severity = variantToSeverity[variant];
    if (severity) {
      setSeverity(severity);
      props.onChanged(severity);
    }
  };

  const isSeverityDisabled = () => {
    return (
      !issue ||
      issue.type === IssueType.Feedback ||
      issue.type === IssueType.Routine ||
      // Campaign Issues show the pink 'campaign' type but it is not changeable.
      issue.type === IssueType.Campaign
    );
  };

  // Campaign Issues always render the pink 'campaign' variant regardless of
  // severity (which is BLUE on dispatch); everything else maps from severity.
  const typeVariant: TypeVariant =
    issue?.type === IssueType.Campaign
      ? 'campaign'
      : severityToVariant[currentSeverity];

  const handleBackPress = async () => {
    if (props.onBackPress) {
      props.onBackPress();
    } else {
      await onSlideChange(SlideAction.SINGLE);
      // navigate('..') is route-relative back — always safe regardless of
      // browser history depth. navigate(-1) was context-unaware and fragile.
      navigate('..');
    }
  };

  const handleAssetPress = async () => {
    if (props.onAssetPress) {
      props.onAssetPress();
    }
  };

  const onEditPress = () => {
    if (location.pathname.includes('/edit')) {
      return;
    }

    const editPath = location.pathname;
    const opensInsideActivitiesShell = /^\/activities\/[^/]+\/?$/.test(
      editPath
    );

    if (opensInsideActivitiesShell) {
      navigate(`${editPath}/edit`);
      return;
    }

    prepareSlideTransition(SlideAction.NEXT);
    navigate(`${editPath}/edit`, {
      state: { slideLayoutInitialTransition: SlideAction.NEXT },
    });
  };

  if (!issue) {
    return;
  }

  const showAssetLink =
    props.showAssetLink !== false &&
    !!issue.asset &&
    !!props.onAssetPress &&
    // Don't show the asset link when Issue occupies the leftmost column:
    // there's nowhere to slide into and the link would be a dead button.
    // A caller whose handler navigates to a route instead of sliding
    // (the share views) opts back in with `forceShowAssetLink`.
    (column !== 'left' || !!props.forceShowAssetLink);

  return (
    <View style={styles.bar}>
      <Pressable
        onPress={handleBackPress}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={t('topNav.back')}
        testID="conversation-back"
      >
        <Icon name="Chevron Left" size={20} color={Colors.role.textStrong} />
      </Pressable>

      <TypeDropdown
        value={typeVariant}
        onChange={handleSeverityChange}
        disabled={isSeverityDisabled() || !!props.readOnly}
        showLabel={false}
        testID="issue-detail-severity-dropdown"
        availableTypes={['urgent', 'pending', 'solved']}
      />

      <View style={styles.titleGroup}>
        <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
          {issue.title}
        </Text>
        {showAssetLink ? (
          // The asset's name is the way to the asset: one row has no room for a
          // second label saying the same thing.
          <Pressable
            style={styles.assetLink}
            onPress={handleAssetPress}
            accessibilityRole="link"
            accessibilityLabel={
              props.assetButtonLabel ?? t('assets.menu.titleSingular')
            }
            testID="conversation-asset-button"
          >
            <Text
              style={styles.subtitle}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {issue.asset?.name || issue.workspace?.name}
            </Text>
            <Icon
              name="Chevron Right"
              size={12}
              color={Colors.role.textSecondary}
            />
          </Pressable>
        ) : (
          <Text style={styles.subtitle} numberOfLines={1} ellipsizeMode="tail">
            {issue.asset?.name || issue.workspace?.name}
          </Text>
        )}
      </View>

      <View style={styles.actions}>
        {props.onCallPress && !props.readOnly && (
          <CallModeButton
            onStart={props.onCallPress}
            testID="activity-header-call"
          />
        )}
        {!props.hideMenu && !props.readOnly && (
          <>
            {issue.isPinned && (
              <MaterialCommunityIcons
                name="pin"
                color={Colors.role.textStrong}
                size={20}
                style={styles.pinnedIcon}
              />
            )}
            <Pressable onPress={onEditPress}>
              <Icon name="Settings" size={20} color={Colors.role.textStrong} />
            </Pressable>
            {(props.onShare || props.onPrint) && (
              <Menu
                visible={menuVisible}
                onDismiss={() => setMenuVisible(false)}
                contentStyle={styles.menu}
                anchor={
                  <Pressable
                    onPress={() => setMenuVisible(true)}
                    hitSlop={8}
                    accessibilityRole="button"
                    accessibilityLabel={t(
                      'activity.menu.moreActions',
                      'More actions'
                    )}
                    testID="issue-header-overflow"
                  >
                    <MaterialCommunityIcons
                      name="dots-vertical"
                      size={20}
                      color={Colors.role.textStrong}
                    />
                  </Pressable>
                }
              >
                <ActionMenuList>
                  {props.onShare && (
                    <ActionMenuRow
                      iconName="Share"
                      label={t('share.menuItem', 'Share')}
                      accessibilityLabel={t('share.menuItem', 'Share')}
                      onPress={() => {
                        setMenuVisible(false);
                        props.onShare?.();
                      }}
                      testID="issue-header-share"
                    />
                  )}
                  {props.onPrint && (
                    <ActionMenuRow
                      iconName="Print"
                      label={t('activity.menu.print', 'Print')}
                      accessibilityLabel={t('activity.menu.print', 'Print')}
                      onPress={() => {
                        setMenuVisible(false);
                        props.onPrint?.();
                      }}
                      testID="issue-header-print"
                    />
                  )}
                </ActionMenuList>
              </Menu>
            )}
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  menu: {
    backgroundColor: Colors.role.surfacePopover,
    borderRadius: tokens.borderRadius.md,
    paddingVertical: 0,
  },
  bar: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.lg,
    backgroundColor: Colors.role.canvas,
    zIndex: 2,
  },
  titleGroup: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
    justifyContent: 'center',
  },
  title: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.role.textStrong,
  },
  subtitle: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
    flexShrink: 1,
  },
  actions: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.lg,
  },
  assetLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    minWidth: 0,
  },
  pinnedIcon: {
    width: 20,
    height: 20,
    backfaceVisibility: 'hidden',
  },
});
